import React from "react";

import { IgSelect, IgTextField, IgTypography } from "@intelligems/ig-theme";
import {
  FontStyleOptions,
  MessageBoxWidgetDto,
  OfferDto,
  ProgressBarWidgetDto,
  QuantityButtonsWidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import { Grid, InputAdornment, MenuItem } from "@mui/material";
import {
  isMessageBox,
  isQuantityButtons,
} from "@src/store/slices/widgets/util";

import { hasFreeShipping, hasGiftWithPurchase } from "../utils/offers";

interface TypographySectionProps {
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto;
  typography: any;
  onTypographyChange: (
    field: string,
    property: "fontSize" | "fontStyle",
    value: number | string
  ) => void;
  offer?: OfferDto;
  quantityButtonVariant?: string;
  disabled?: boolean;
}

export const TypographySection = ({
  widget,
  typography,
  onTypographyChange,
  offer,
  quantityButtonVariant,
  disabled,
}: TypographySectionProps) => {
  const offerHasFreeShipping = hasFreeShipping(offer);

  const offerHasGiftWithPurchase = hasGiftWithPurchase(offer);
  const renderTypographyItem = ([key, value]: [string, any]) => (
    <Grid item container xs={6} key={key}>
      <Grid item container direction="column" spacing={2}>
        <Grid item>
          <IgTypography>
            {key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())}
          </IgTypography>
        </Grid>
        <Grid item container columnSpacing={2} alignItems="center">
          <Grid item xs={6}>
            <IgTextField
              disabled={disabled}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">px</InputAdornment>
                ),
              }}
              labelProps={{ variant: "body2" }}
              label={"Font size"}
              value={parseInt(value?.fontSize) || 12}
              onChange={(e) => {
                const fontSize = parseInt(e.target.value) || 12;
                onTypographyChange(key, "fontSize", `${fontSize}px`);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <IgSelect
              label={"Font style"}
              disabled={disabled}
              labelProps={{ variant: "body2" }}
              size="small"
              fullWidth
              value={value?.fontStyle || "regular"}
              onChange={(e) =>
                onTypographyChange(
                  key,
                  "fontStyle",
                  e.target.value || "regular"
                )
              }
            >
              {FontStyleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </IgSelect>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderTypography = () => {
    const enhancedTypography = { ...typography };

    // Only add free shipping typography if offer has free shipping
    if (offerHasFreeShipping) {
      if (!enhancedTypography.freeShippingLabel) {
        enhancedTypography.freeShippingLabel = {
          fontSize: "12px",
          fontStyle: "regular",
        };
      }
    } else {
      // Remove free shipping typography if offer doesn't have free shipping
      delete enhancedTypography.freeShippingLabel;
    }

    // Only add gift typography if offer has gift with purchase
    if (offerHasGiftWithPurchase) {
      if (!enhancedTypography.freeGiftLabel) {
        enhancedTypography.freeGiftLabel = {
          fontSize: "12px",
          fontStyle: "regular",
        };
      }
      if (!enhancedTypography.freeGiftCompareAtPrice) {
        enhancedTypography.freeGiftCompareAtPrice = {
          fontSize: "11px",
          fontStyle: "regular",
        };
      }
    } else {
      // Remove gift typography if offer doesn't have gift with purchase
      delete enhancedTypography.freeGiftLabel;
      delete enhancedTypography.freeGiftCompareAtPrice;
    }

    if (quantityButtonVariant === "quantityButtonsWidgetCompact") {
      delete enhancedTypography.badge;
      delete enhancedTypography.subtitle;
    }

    return (
      <Grid item container spacing={4}>
        {Object.entries(enhancedTypography).map(renderTypographyItem)}
      </Grid>
    );
  };

  const renderQuantityButtonsTypography = () => renderTypography();

  const renderProgressBarTypography = () => {
    const progressBarTypography = {
      dynamicProgressText: typography?.dynamicProgressText || {
        fontSize: "14px",
        fontStyle: "regular",
      },
      breakpointText: typography?.breakpointText || {
        fontSize: "12px",
        fontStyle: "regular",
      },
    };

    return (
      <Grid item container spacing={3}>
        {Object.entries(progressBarTypography).map(renderTypographyItem)}
      </Grid>
    );
  };

  const renderMessageBoxTypography = () => {
    const messageBoxTypography = {
      title: typography?.title || {
        fontSize: "16px",
        fontStyle: "medium",
      },
      description: typography?.description || {
        fontSize: "14px",
        fontStyle: "regular",
      },
    };

    return (
      <Grid item container spacing={3}>
        {Object.entries(messageBoxTypography).map(renderTypographyItem)}
      </Grid>
    );
  };

  return (
    <Grid item container direction="column" spacing={5}>
      <Grid item>
        <IgTypography variant="body1" fontWeight="600">
          Typography
        </IgTypography>
      </Grid>
      <Grid item container>
        {isQuantityButtons(widget)
          ? renderQuantityButtonsTypography()
          : isMessageBox(widget)
            ? renderMessageBoxTypography()
            : renderProgressBarTypography()}
      </Grid>
    </Grid>
  );
};
